import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ResetPasswordMethodEnum } from '@core/enums/auth';
import { IData } from '@core/interfaces/data.interface';
import { IUser } from '@core/interfaces/user.Interface';
import { TokenManager } from '@core/services/tokens/token-manager';
import { endPoints } from '@core/utils/constants';
import { environment } from '@env/environment';
import { catchError, EMPTY, map, of, tap } from 'rxjs';
import { API_URL } from './tokens/api-url.token';
import { ICentroCapitado } from '@core/interfaces/centro-capitado.interface';

@Injectable({ providedIn: 'root' })
export class AuthService {

  private _user: IUser | null = null;

  get user() {
    return this._user as IUser;
  }

  constructor(
    @Inject(API_URL) private readonly apiUrl: string,
    private readonly http: HttpClient
  ) { }

  login(model: any) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/${endPoints.AUTH}/${endPoints.LOGIN}`;

    // Crear los datos en formato x-www-form-urlencoded
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('username', model.username);
    urlEncodedData.append('password', model.password);

    // Configurar los encabezados
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/x-www-form-urlencoded')

    return this.http.post<IData<{
      token: string;
      refreshToken: string;
      usuarioId: number;
      usuario: any;
    }>>(url, urlEncodedData.toString(), { headers }).pipe(
      tap(({ data }) => {
        TokenManager.set(model.username, TokenManager.USERNAME);
        (
          Object.entries(data)
            .filter(([key]) => /[T|t]oken/.test(key))
            .forEach(([key, token]) => TokenManager.set(token.toString(), key))
        )
      })
    );
  }

  confirmUser(model: any) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/${endPoints.AUTH}/gestion-alta-usuario`;

    return this.http.post(url, model);
  }

  getCentrosByEntidadId(entidadId: number) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/centros-capitados/${entidadId}/centros-capitados`;

    return this.http
      .get<IData<ICentroCapitado[]>>(url)
      .pipe(
        map(({ data }) => data),
        catchError(() => of([]))
      );
  }

  resetPassword(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/usuario/recuperar-contrasena`;
    return this.http.post<IData<any>>(url, model);
  }

  confirmResetPassword(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/usuario/confirmar-recuperar-contrasena`;
    return this.http.post<IData<any>>(url, model);
  }

  updatePassword(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.AUTH}/${endPoints.UPDATE_PASSWORD}`;
    return this.http.put(url, model);
  }


  changePassword(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.AUTH}/${endPoints.CHANGE_PASSWORD}`;
    const headers = { [TokenManager.HEADER_TYPE]: `${TokenManager.HEADER_PREFIX} ${TokenManager.get(TokenManager.TOKEN_ID)}` };

    return this.http.post(url, model, { headers });
  }

  resendPasswordCode(model: FormData) {
    const url = `${this.apiUrl}/internal/auth/actualizar-clave`;
    model.append('esRegenerarCodigoOTP', JSON.stringify(true));

    return this.http.put<IData<{
      numeroTelefonoEnmascarado?: string;
      claveUsuarioActualizado: boolean;
      tipoMedioReinicioClaveNuevo: ResetPasswordMethodEnum;
      tiposMedioReinicioClavePermitidos: ResetPasswordMethodEnum[];
      otpRegenerado: {
        solicitudGestionUsuarioId: number;
        tokenId: string;
      }
    }>>(url, model);
  }

  logout(force: boolean = true) {
    this._user = null;
    TokenManager.removeToken();
    force && location.reload();

    return EMPTY;
  }

  refreshToken() {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/auth/refresh-token`;
    const headers = new HttpHeaders()
      .append('x-api-key', environment.API_KEY as string)
      .append('refresh-token', TokenManager.get(TokenManager.REFRESH_TOKEN) as string);

    return this.http.get<IData<{
      tokenId: string;
      accessToken: string;
    }>>(url, { headers }).pipe(
      tap(({ data }) => (
        Object.entries(data)
          .filter(([key]) => /[T|t]oken/.test(key))
          .forEach(([key, token]) => TokenManager.set(token as string, key))
      ))
    );
  }

  resolve(opts?: { force: boolean }) {
    if (this._user && !opts?.force) {
      return of(this._user);
    }

    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.USER}/${endPoints.ME}`;

    return this.http.get<IData<IUser>>(url).pipe(
      map(x => x.data),
      tap(user => this._user = user)
    );
  }
}
